import { Row, Col, Form } from 'antd';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import TaskTypeItem from './components/TaskTypeItem';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import UserInfoContext from '@/layout/context';
import { globalExpansionTaskAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import FormManager from '@/views/expandstore/pages/expansionFranchiseeCircleTask/components/FormManager';
import { TaskStatus } from '../../../ts-config';
import { standardTaskAssign } from '@/common/api/expandStore/expansiontask';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

interface Props {
  title?:string
  detail: any;
  /** 拓店任务详情抽屉是否打开 */
  open: boolean;
  /** 是否可变更类型 */
  canEditType?: boolean;
  refresh?: Function;
  /** 分享时调用接口需要的数据，如果有值代表“分享时” */
  shareData?,
  /** 是否隐藏加盟商唯一标识字段 */
  hideFranchiseeUniqueId?: boolean;
}

/**
 * 拓店任务-详情-基本信息
 * @Description 拓店任务：加盟（益禾堂、非益禾堂）
 */
const TaskBasic: FC<Props> = ({
  title,
  detail,
  open = false, // 拓店任务详情抽屉是否打开
  canEditType = false, // 是否可变更类型
  refresh,
  // shareData, // 分享时调用接口需要的数据，如果有值代表“分享时”
  hideFranchiseeUniqueId = false,
}) => {
  const [form] = Form.useForm();
  const [franDetail, setFranDetail] = useState<any>({});
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);

  useEffect(() => {
    if (detail && !detail.franchiseeDetail && detail.standardFranchiseeId) {
      // 有拓店任务数据，但未包含加盟商信息，需要额外请求加盟商详情
      // 机会点详情中拓店任务模块属于这种情况
      getFranchiseeDetail({ id: detail.standardFranchiseeId }).then(data => {
        isNotEmptyAny(data) && setFranDetail(data);
      });
    }
  }, [detail]);

  const infos = useMemo(() => {
    return [
      { label: `${expansionTaskAlias || '任务'}名称`, value: detail?.name },
      /* 任务类型Item，自定义node */
      { node: <TaskTypeItem
        detail={detail}
        open={open}
        canEditType={canEditType}
        refresh={refresh}
        expansionTaskAlias={expansionTaskAlias}
      /> },
      { label: '签约类型', value: detail?.signTypeName },
      { label: `${franchiseeAlias}姓名`, value: detail?.franchiseeDetail?.name || franDetail?.name },
      !hideFranchiseeUniqueId ? { label: detail?.franchiseeDetail?.uniqueName || franDetail?.uniqueName || `${franchiseeAlias}唯一标识`, value: detail?.franchiseeDetail?.uniqueId || franDetail?.uniqueId } : null,
      { label: '目标城市', value: detail?.targetArea },
      { label: '第一意向区域', value: detail?.firstArea },
      { label: '第二意向区域', value: detail?.secondArea },
      { label: '期望落位日期', value: detail?.expectDropInDate },
      /* 机会点详情拓店任务信息模块返回的是developManage */
      { label: '开发人员', key: 'manager', value: detail?.manager || detail?.developManager },
    ].filter(Boolean);
  }, [detail, franDetail, expansionTaskAlias, hideFranchiseeUniqueId]);

  /**
   * 设置detail item 是否能编辑
   * @param key
   * @returns
   */
  const getDetailItemAllowEdit = (key) => {
    if (key === 'manager') {
      // 待指派时才可选择
      return detail?.status === TaskStatus.WaitAssign;
    }
    return false;
  };

  /**
   * 设置detail item 编辑内容
   * @param key
   * @returns
   */
  const getDetailItemEditConfig = (key) => {
    if (key === 'manager') {
      return {
        formCom: <FormManager label='开发人员' placeholder='请选择开发人员' name='manager'/>,
        onCancel() {},
        onOK: async() => {
          const values = await form.validateFields();
          const val = values['manager'];
          console.log('选择manager', val);
          if (!val) return;
          await standardTaskAssign({
            id: detail?.id,
            assignAccountIds: [val]
          });
          V2Message.success('指派成功');
          refresh?.();
        }
      };
    }
    return undefined;
  };

  return detail ? (
    <>
      {title && <V2Title
        type='H2'
        text={title}
        divider
        className={'pt-16'}
      />}
      <V2Form form={form}>
        <Row gutter={16}>
          {
            isArray(infos) ? infos.map(({ label, value, key, node }: any, index: number) => (
              <Col span={8} key={index}>
                { node || <V2DetailItem
                  label={label}
                  value={value}
                  allowEdit={getDetailItemAllowEdit(key)}
                  editConfig={ getDetailItemEditConfig(key) }
                />}
              </Col>
            )) : <></>
          }
        </Row>
      </V2Form>
    </>
  ) : (
    <></>
  );
};

export default TaskBasic;
